import { css } from 'aphrodite';
import { Field, FormProps, reduxForm, SubmissionError } from 'redux-form';
import * as _ from 'lodash';
import * as React from 'react';
import autobind from 'autobind-decorator';

import { animationStyles, paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import { requestPasswordReset } from 'login-apis/accountRequestAPI';
import * as formUtils from 'login-utils/formUtils';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import InputMap from 'login-components/Common/Inputs/InputMap';
import PrimaryButton from 'login-components/Common/Buttons/PrimaryButton';

const FORM_ERROR_ID = 'RequestPasswordResetForm-formError';
const input: FormInput = {
  ...InputMap.EmailInput,
  name: 'email',
  identifier: 'email',
  label: 'Email',
};

type RequestPasswordResetProps = FormProps<any, any, any>;

interface RequestPasswordResetState {
  submitSuccess: boolean;
  successText?: string | null;
}

@autobind
export class RequestPasswordReset extends React.Component<RequestPasswordResetProps, RequestPasswordResetState> {
  constructor(props) {
    super(props);
    this.state = {
      successText: null,
      submitSuccess: false,
    };
  }

  async submitPasswordReset(values) {
    this.setState({
      submitSuccess: false,
    });
    try {
      await requestPasswordReset(values);
      this.setState({
        successText: 'Sent! Check your email.',
        submitSuccess: true,
      });
    } catch (err) {
      this.setState({
        successText: null,
        submitSuccess: false,
      });

      const responseData = _.get(err, 'response.data'); // might be an object or undefined
      const errorText = _.isString(responseData) ? responseData : 'There was an error requesting the password reset.';

      throw new SubmissionError({ _error: errorText });
    }
  }

  render() {
    const { error, handleSubmit, submitting } = this.props;
    const { submitSuccess, successText } = this.state;
    const btnText = submitSuccess || submitting ? 'Resend' : 'Send link';

    return (
      <div className="RequestPasswordReset">
        <form
          className={css(paneFormStyles.container)}
          id="it-RequestPasswordResetForm"
          noValidate
          onSubmit={handleSubmit!(this.submitPasswordReset)}
        >
          <div>
            <fieldset className={css(paneFormStyles.fieldWrapper)}>
              <Field key={input.name} {...input} hidePlaceholder={true} />
            </fieldset>
            {error && (
              <div className={css(paneFormStyles.errorWrapper)}>
                <ErrorMessage errorText={error} errorId={FORM_ERROR_ID} />
              </div>
            )}
          </div>
          <div
            data-it="RequestPasswordReset-successText"
            className={css(paneFormStyles.successText, submitSuccess && animationStyles.slideandFade)}
          >
            {successText}
          </div>
          <div className={css(paneFormStyles.buttonDisplay, paneFormStyles.buttonPadding)}>
            <PrimaryButton
              isWide={true}
              disabled={submitting}
              buttonId="it-RequestPasswordResetSubmit"
              text={btnText}
            />
          </div>
        </form>
      </div>
    );
  }
}

const ConnectedRequestPasswordReset = reduxForm({
  form: 'passwordReset',
  validate: _.partial(formUtils.validate, [input]) as any,
  onSubmitFail: syncErrors => formUtils.focusOnSubmitFail(syncErrors, FORM_ERROR_ID),
})(RequestPasswordReset);

export default ConnectedRequestPasswordReset;
